import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useT } from '../i18n/index.js';
import {
  consent,
  OFFERED_CATEGORIES,
  type ConsentChoice,
  type ConsentSnapshot,
  type OptionalCategory,
} from './consent.js';
import './consent.css';

/**
 * The cookie question, asked once, at the foot of every page.
 *
 * It is a strip, not a wall: the page stays readable and usable behind it,
 * because nothing it asks about is needed for the page to work. "Reject" and
 * "Accept" are the same button twice, same size, same colour, same place in
 * the reading order, because the law asks for refusing to be exactly as easy
 * as agreeing and because a visitor should not have to hunt for the no.
 *
 * Whatever else is pinned to the bottom of the viewport makes room for it
 * rather than the other way round: while it is up, its height is published as
 * `--consent-h` on the root and the landing page's sign-up bar and guide lift
 * by that much. On a phone inside the app it sits on top of the tab bar.
 *
 * It is only ever up where the privacy model asks first (see consent.ts), and
 * only once `/api/geo` has said so: in the US model there is no banner, and
 * "Privacy choices" in the footer opens the same dialog instead.
 */

const NO_SNAPSHOT: ConsentSnapshot = { record: null, region: null };

function useConsentSnapshot(): ConsentSnapshot {
  return useSyncExternalStore(consent.subscribe, consent.getSnapshot, () => NO_SNAPSHOT);
}

/** True in the US model, where the link and the dialog speak of privacy choices rather than cookies. */
function useUsModel(): boolean {
  return useConsentSnapshot().region?.regime === 'us';
}

/** What the footer link and the account menu item are called where the visitor is. */
export function usePrivacyChoicesLabel(): string {
  const t = useT();
  return useUsModel() ? t('consent.us.footerLink') : t('consent.footerLink');
}

export function ConsentBanner() {
  const t = useT();
  const snapshot = useConsentSnapshot();
  const [prefsOpen, setPrefsOpen] = useState(false);
  const banner = useRef<HTMLElement>(null);
  // Re-read on every snapshot: the region arriving is what can raise it.
  const pending = snapshot.region !== null && consent.needsAnswer();
  /*
   * Not over the boot splash: a cookie question on an empty page made the
   * app look broken while the session probe was still out.
   */
  const booting = useBooting();

  useEffect(() => consent.onOpenRequest(() => setPrefsOpen(true)), []);

  /*
   * Publish the strip's height for the other things pinned to the bottom.
   * A ResizeObserver rather than one measurement, because the copy wraps
   * differently in Turkish and when the window is resized.
   */
  useLayoutEffect(() => {
    const root = document.documentElement;
    const node = banner.current;
    if (!pending || booting || !node) {
      root.style.removeProperty('--consent-h');
      delete root.dataset['consent'];
      return;
    }
    root.dataset['consent'] = 'pending';
    const publish = () => {
      // The 12px is the gap kept between the strip and whatever stacks above it.
      root.style.setProperty('--consent-h', `${Math.ceil(node.offsetHeight) + 12}px`);
    };
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(node);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--consent-h');
      delete root.dataset['consent'];
    };
  }, [pending, booting]);

  return (
    <>
      {pending && !booting ? (
        <section ref={banner} className="cc-banner" aria-label={t('consent.label')}>
          <div className="cc-copy">
            <p>{t('consent.body')}</p>
            <p className="cc-links">
              <a href="/legal/cookies">{t('consent.cookiePolicy')}</a>
              <a href="/legal/privacy">{t('consent.privacy')}</a>
              <button type="button" className="cc-linkbutton" onClick={() => setPrefsOpen(true)}>
                {t('consent.preferences')}
              </button>
            </p>
          </div>
          <div className="cc-actions">
            <button type="button" className="cc-button" onClick={consent.rejectAll}>
              {t('consent.reject')}
            </button>
            <button type="button" className="cc-button" onClick={consent.acceptAll}>
              {t('consent.accept')}
            </button>
          </div>
        </section>
      ) : null}
      <ConsentPreferences open={prefsOpen} onOpenChange={setPrefsOpen} />
    </>
  );
}

/**
 * The detailed choice: one switch per kind, necessary always on. Where the
 * model asks first the rest start off; in the US model they start as they are
 * in force (on, unless opted out or GPC). Opening it again later starts from
 * what is in force, not from a half-made change.
 */
function ConsentPreferences({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const t = useT();
  const snapshot = useConsentSnapshot();
  const us = snapshot.region?.regime === 'us';
  const gpc = snapshot.region?.gpc === true;
  const rows: ('necessary' | OptionalCategory)[] = ['necessary', ...OFFERED_CATEGORIES];
  const [draft, setDraft] = useState<ConsentChoice>({
    preferences: false,
    analytics: false,
    marketing: false,
  });

  // Every opening starts from what is in force, not from a half-made change
  // that was closed without saving.
  useEffect(() => {
    if (!open) return;
    setDraft(consent.effectiveChoice());
  }, [open, snapshot]);

  const finish = (choice: ConsentChoice) => {
    consent.set(choice);
    onOpenChange(false);
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="cc-overlay" />
        <DialogPrimitive.Content className="cc-dialog" aria-modal="true">
          <div className="cc-dialog-head">
            <DialogPrimitive.Title className="cc-dialog-title">
              {us ? t('consent.us.dialog.title') : t('consent.dialog.title')}
            </DialogPrimitive.Title>
            <DialogPrimitive.Close className="cc-close" aria-label={t('common.close')}>
              <svg viewBox="0 0 16 16" aria-hidden="true">
                <path d="m4 4 8 8m0-8-8 8" />
              </svg>
            </DialogPrimitive.Close>
          </div>
          <DialogPrimitive.Description className="cc-dialog-lede">
            {us ? t('consent.us.dialog.body') : t('consent.dialog.body')}
            {gpc ? ` ${t('consent.gpc')}` : ''}{' '}
            <a href="/legal/cookies">{t('consent.cookiePolicy')}</a>
          </DialogPrimitive.Description>

          <ul className="cc-rows">
            {rows.map((id) => {
              const necessary = id === 'necessary';
              const checked = necessary ? true : draft[id];
              const labelId = `cc-row-${id}`;
              return (
                <li key={id} className="cc-row">
                  <div className="cc-row-text">
                    <strong id={labelId}>{t(`consent.category.${id}`)}</strong>
                    <span>
                      {us && id === 'analytics'
                        ? t('consent.us.category.analytics.body')
                        : t(`consent.category.${id}.body`)}
                    </span>
                  </div>
                  {necessary ? (
                    <span className="cc-always">{t('consent.alwaysOn')}</span>
                  ) : (
                    <button
                      type="button"
                      role="switch"
                      aria-checked={checked}
                      aria-labelledby={labelId}
                      className="cc-switch"
                      onClick={() => setDraft((current) => ({ ...current, [id]: !current[id] }))}
                    >
                      <span aria-hidden="true" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>

          <div className="cc-dialog-actions">
            <button
              type="button"
              className="cc-button cc-button-quiet"
              onClick={() => finish({ preferences: false, analytics: false, marketing: false })}
            >
              {t('consent.rejectAll')}
            </button>
            <button
              type="button"
              className="cc-button cc-button-quiet"
              onClick={() => finish({ preferences: true, analytics: true, marketing: false })}
            >
              {t('consent.acceptAll')}
            </button>
            <button type="button" className="cc-button cc-save" onClick={() => finish(draft)}>
              {t('consent.save')}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** The "Cookie preferences" (or, in the US model, "Privacy choices") link a footer carries. */
export function ConsentLink({ className }: { className?: string }) {
  const label = usePrivacyChoicesLabel();
  return (
    <button
      type="button"
      className={className ? `cc-footer-link ${className}` : 'cc-footer-link'}
      onClick={consent.requestOpen}
    >
      {label}
    </button>
  );
}

/** True while the app still shows its boot splash. */
function useBooting(): boolean {
  // Assume booting until the first commit has shown whether a splash is up.
  const [booting, setBooting] = useState(true);
  useEffect(() => {
    if (!booting) return;
    const observer = new MutationObserver(() => {
      if (!document.querySelector('.boot-splash')) setBooting(false);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    if (!document.querySelector('.boot-splash')) setBooting(false);
    return () => observer.disconnect();
  }, [booting]);
  return booting;
}
